import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2 } from 'lucide-react';
import { supabase } from '../../../src/lib/supabase';
import { useToast } from '../../../src/contexts/ToastContext';
import { useAuth } from '../../../src/contexts/AuthContext';
import { logUsageEvent } from '../../../src/lib/logger';
import { UcParaRateio } from '../../../src/hooks/useRateioBalanceamento';

interface GdModalidadeModalProps {
  uc: UcParaRateio;
  onClose: () => void;
  onSaved: () => void;
}

/**
 * Define a modalidade GD I / GD II da UC (Seção 14 da spec — tag ao lado do
 * nome da UC). Só cadastro/exibição — não alimenta nenhum cálculo de
 * faturamento mínimo (isso vive fora do módulo de rateio).
 */
const GdModalidadeModal: React.FC<GdModalidadeModalProps> = ({ uc, onClose, onSaved }) => {
  const { addToast } = useToast();
  const { user } = useAuth();
  const [modalidade, setModalidade] = useState<'GD_I' | 'GD_II' | null>(uc.modalidade_gd ?? null);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { error } = await supabase.rpc('definir_modalidade_gd', { p_uc_id: uc.id, p_modalidade: modalidade });
      if (error) throw error;
      addToast('Modalidade GD atualizada.', 'success');
      logUsageEvent({ userId: user?.id, modulo: 'gestao', funcionalidade: 'rateio_simulador', acao: 'definir_modalidade_gd', origem: 'acao' });
      onSaved();
    } catch (error: any) {
      console.error('Error setting GD modalidade', error);
      addToast(error?.message || 'Erro ao atualizar a modalidade GD.', 'error');
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[2200] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-sm">
        <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-gray-700">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white font-poppins">Modalidade GD — {uc.numero_uc}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-2">
          {(
            [
              { value: null, label: 'Não definido' },
              { value: 'GD_I', label: 'GD I' },
              { value: 'GD_II', label: 'GD II' },
            ] as const
          ).map(opt => (
            <label
              key={opt.label}
              className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-900 cursor-pointer"
            >
              <input
                type="radio"
                name="modalidade_gd"
                checked={modalidade === opt.value}
                onChange={() => setModalidade(opt.value)}
              />
              {opt.label}
            </label>
          ))}
          <p className="text-xs text-gray-500 dark:text-gray-400 pt-2">
            Só cadastro/exibição — não afeta nenhum cálculo de faturamento.
          </p>
        </div>

        <div className="flex justify-end gap-3 p-6 border-t border-gray-100 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 rounded-lg bg-primary text-white font-medium hover:opacity-90 disabled:opacity-60 flex items-center gap-2"
          >
            {saving && <Loader2 size={16} className="animate-spin" />}
            Salvar
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default GdModalidadeModal;
